<template>
  <div v-if="cmSectionState !== 'empty'">
    <div v-if="loading" class="cm-state">{{ $t('CM_LOADING') }}</div>
    <div v-else-if="error" class="cm-state is-error">{{ error }}</div>

    <template v-else-if="data">
      <Cm-section-body block="summary" class="cm-cards">
        <div class="panel">
          <div class="cm-card-value">{{ formatNum(data.totals.posts_count) }}</div>
          <div class="cm-card-label">{{ $t(postsCardLabel) }}</div>
        </div>
        <div class="panel">
          <div class="cm-card-value">{{ formatNum(data.totals.unique_terms) }}</div>
          <div class="cm-card-label">{{ $t('CM_CARD_TERMS') }}</div>
        </div>
        <div class="panel">
          <div class="cm-card-value">
            {{ data.totals.pages_with_posts }} / {{ data.totals.pages_total }}
          </div>
          <div class="cm-card-label">{{ $t(pagesCardLabel) }}</div>
        </div>
        <div class="panel">
          <div class="cm-card-value">{{ formatNum(data.totals.pages_total) }}</div>
          <div class="cm-card-label">{{ $t('CM_CARD_PAGES_TOTAL') }}</div>
        </div>
      </Cm-section-body>

      <Cm-section-head
        v-if="hasTopicsContent"
        numbered
        collapsible
        section-id="cm-topics"
        :number="topicsNumber"
        :title="$t('CM_SEC_TOPICS')"
        :description="$t('CM_SEC_TOPICS_DESC')"
        block="topics"
      />

      <Cm-section-body block="topics">
        <div :class="topGridClass">
          <Cm-top-table
            v-if="hasTop('by_posts')"
            :title="$t('CM_TOP_USED')"
            :hint="$t('CM_TOP_USED_HINT')"
            :rows="data.tops.by_posts"
            :tracked="data.tracked_terms"
            :variant="data.discussion_available ? 'both' : 'er'"
            tone="indigo"
          />
          <Cm-top-table
            v-if="relativeTabs.length"
            :title="$t(relativeTable.title)"
            :hint="$t(relativeTable.hint)"
            :rows="relativeTable.rows"
            :tracked="data.tracked_terms"
            :variant="relativeTable.variant"
            :tone="relativeTable.tone"
          >
            <template v-slot:header-controls>
              <div class="cm-seg">
                <button
                  v-for="tab in relativeTabs"
                  :key="tab.key"
                  :class="{ active: activeRelativeTab === tab.key }"
                  @click="activeRelative = tab.key"
                >
                  {{ $t(tab.shortLabel) }}
                </button>
              </div>
            </template>
          </Cm-top-table>
        </div>

        <div :class="phrasesTrendsClass">
          <Cm-top-table
            v-if="showPhrases"
            :title="$t('CM_PHRASES')"
            :hint="$t('CM_PHRASES_HINT')"
            :rows="data.phrases"
            :variant="data.discussion_available ? 'both' : 'er'"
            tone="violet"
          />
          <Cm-trends
            v-if="showTrends"
            :pages="pages"
            :scope="scope"
            @state="forwardSectionState"
          />
        </div>
      </Cm-section-body>
    </template>
  </div>
</template>

<script>
import cmScopeMixin from "@/mixins/cmScopeMixin";
import cmSectionMixin from "@/mixins/cmSectionMixin";
import cmApi from "@/api/controllers/content-monitoring";
import pageUtils from "@/models/page-utils";
import { cmHeadVisibility, cmHeadNumber } from "@/models/content-monitoring/section-heads";
import CmTopTable from "./Cm-top-table";
import CmTrends from "./Cm-trends";
import CmSectionHead from "./Cm-section-head";
import CmSectionBody from "./Cm-section-body";

export default {
  name: "Cm-overview",
  mixins: [cmScopeMixin, cmSectionMixin],
  components: { CmTopTable, CmTrends, CmSectionHead, CmSectionBody },

  data() {
    return {
      data: null,
      loading: false,
      error: "",
      activeRelative: "er",
      trendsState: "loading",
    };
  },

  computed: {
    hasData() {
      return !!(this.data && this.data.totals && this.data.totals.posts_count > 0);
    },

    /**
     * На вкладке картинок карточки считают не все посты, а те, у которых есть
     * разобранная картинка (так же сужена и лента) — подпись должна об этом
     * говорить, иначе число выглядит как «в разделе пропали посты».
     */
    postsCardLabel() {
      return this.getCmFormat === "media" ? "CM_CARD_POSTS_MEDIA" : "CM_CARD_POSTS";
    },

    pagesCardLabel() {
      return this.getCmFormat === "media"
        ? "CM_CARD_PAGES_WITH_POSTS_MEDIA"
        : "CM_CARD_PAGES_WITH_POSTS";
    },

    topGridClass() {
      if (!this.data) return null;
      const count =
        (this.hasTop("by_posts") ? 1 : 0) +
        (this.relativeTabs.length ? 1 : 0);
      if (count === 2) return "cm-grid-2";
      return null;
    },

    relativeTabs() {
      if (!this.data) return [];
      const tabs = [];
      if (this.hasTop("by_er")) {
        tabs.push({ key: "er", shortLabel: "CM_TL_TAB_ER" });
      }
      if (this.data.discussion_available && this.hasTop("by_tr")) {
        tabs.push({ key: "tr", shortLabel: "CM_TL_TAB_TR" });
      }
      return tabs;
    },

    activeRelativeTab() {
      return this.relativeTabs.some((tab) => tab.key === this.activeRelative)
        ? this.activeRelative
        : (this.relativeTabs[0] && this.relativeTabs[0].key);
    },

    relativeTable() {
      const isDiscussion = this.activeRelativeTab === "tr";
      return {
        title: isDiscussion ? "CM_TOP_TR" : "CM_TOP_ER",
        hint: isDiscussion ? "CM_TOP_TR_HINT" : "CM_TOP_ER_HINT",
        rows: this.data.tops[isDiscussion ? "by_tr" : "by_er"] || [],
        variant: isDiscussion ? "tr" : "er",
        tone: isDiscussion ? "teal" : "pink",
      };
    },

    showPhrases() {
      return !!(
        this.data &&
        this.data.phrases &&
        this.data.phrases.length
      );
    },

    showTrends() {
      return this.trendsState !== "empty";
    },

    phrasesTrendsClass() {
      return [
        "cm-phrases-trends",
        { "cm-grid-2": this.showPhrases && this.showTrends },
      ];
    },

    /** Топы, фразы или тренды — иначе нумерованный заголовок не нужен. */
    hasTopicsContent() {
      if (!this.data || this.cmSectionState === "empty") return false;
      const tops = this.hasTop("by_posts") || this.relativeTabs.length;
      return tops || this.showPhrases || this.showTrends;
    },

    topicsNumber() {
      return cmHeadNumber("topics");
    },
  },

  watch: {
    cmScopeKey() {
      this.trendsState = "loading";
    },
    hasTopicsContent: {
      immediate: true,
      handler(visible) {
        cmHeadVisibility.topics = !!visible;
      },
    },
  },

  methods: {
    forwardSectionState(name, state) {
      if (name === "Cm-trends") this.trendsState = state;
      this.$emit("state", name, state);
    },

    hasTop(key) {
      const rows = this.data && this.data.tops && this.data.tops[key];
      return !!(rows && rows.length);
    },

    formatNum(num) {
      return pageUtils.formatNumbers(num);
    },

    load() {
      return this.cmLoad(
        () => cmApi.fetchOverview(this.cmScope, this.scope),
        (data) => {
          this.data = data;
        },
        { silent: true },
      );
    },
  },

  beforeDestroy() {
    cmHeadVisibility.topics = false;
  },
};
</script>
